import { ChangeEvent } from 'react'
import { UploadIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'

import Upload from '@/assets/upload-arrow-tray.svg'
import { ImageData } from '@/requests/signup'
import { convertBase64 } from './utils'

type FileUploaderProps = {
  onUploadComplete: (fileData: ImageData) => void
  onError: (error: string) => void
  customButtonText: string
  showUploadIcon: boolean
  showUploadSVG?: boolean
}

// Helper function to get correct MIME type
const getFileType = (fileName: string): string => {
  const extension = fileName
    .substring(fileName.lastIndexOf('.') + 1)
    .toLowerCase()

  switch (extension) {
    case 'pdf':
      return 'application/pdf'
    case 'png':
      return 'image/png'
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    default:
      return `image/${extension}`
  }
}

export const FileUploader = ({
  onUploadComplete,
  onError,
  customButtonText,
  showUploadIcon,
  showUploadSVG = false,
}: FileUploaderProps) => {
  const { t } = useTranslation()
  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target

    if (!files || files.length === 0) {
      onError('No file selected')
      return
    }

    const selectedFile = files[0]
    const fileName = selectedFile.name

    try {
      const base64 = await convertBase64(selectedFile)
      const fileType = getFileType(fileName)

      onUploadComplete({
        name: fileName,
        type: fileType,
        url: base64 as string,
      })
    } catch {
      onError('Unable to read file. Please check and try again.')
    }
  }

  return (
    <label className="cursor-pointer" data-testid="container-file-uploader">
      <input
        className="invisible mt-[-20px]"
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        onChange={handleUpload}
        data-testid="input-file-uploader"
      />

      <span
        className="uppercase flex flex-col justify-center items-center gap-2 p-5 bg-neutral-300 dark:bg-gravel hover:bg-neutral-200 dark:hover:bg-gray-800"
        data-testid="btn-file-uploader"
      >
        {showUploadSVG && (
          <img
            src={Upload}
            alt="Upload Icon"
            className="w-7 h-7 dark:invert"
            data-testid="img-file-uploader-icon"
          />
        )}
        {customButtonText.length > 0 ? (
          <span data-testid="text-file-uploader-label">{customButtonText}</span>
        ) : (
          <span data-testid="text-file-uploader-default">
            {t('upload.submit')}
          </span>
        )}
        {showUploadIcon && <UploadIcon data-testid="icon-file-uploader" />}
      </span>
    </label>
  )
}
